import { Grid, Divider as MuiDivider, Typography, styled } from '@mui/material';
import {
  BooleanInput,
  Create,
  FormTab,
  ImageField,
  ImageInput,
  PasswordInput,
  TabbedForm,
  TextInput,
  email,
  minLength,
  required,
} from 'react-admin';
import { Permission, PriceInput, Tooltip } from '../../components';
import { CNPJInput, PhoneInput } from '../../components/Inputs';
import { PERMISSIONS } from '../../constants';
import { WrapperDotz } from './ChainEdit';


const validateName = [required(), minLength(2)]
const validateRequired = [required()]
const validateEmail = [required(), email()]
const validatePassword = [required(), minLength(6)];

const validateCredentials = credentials =>
  credentials &&
  credentials.host &&
  credentials.port &&
  credentials.database &&
  credentials.user &&
  credentials.password

const choices = [
  { id: 'utf-8', name: 'UTF-8' },
  { id: 'sql_ascii', name: 'SQL_ASCII' },
]

const Divider = styled(MuiDivider)({
  width: '100%',
  marginTop: 12,
  marginBottom: 12,
});

const RequireLocationInput = () => (
  <BooleanInput
    label="Conferir localização no abastecimento"
    source="requireLocationOnFillin"
    defaultValue={true}
  />
);

export default props => (
  <Create {...props} resource="chains" title="Cadastrar nova rede">
    <TabbedForm sanitizeEmptyValues>
      <FormTab label="Identificação">
        <TextInput source="name" label="Nome da rede/posto" validate={validateName} InputProps={{ autoComplete: 'off' }} sx={{ width: 350 }} fullWidth={false} />
        <PhoneInput
          InputProps={{ autoComplete: 'off' }}
          source="phone"
          label="Telefone"
          validate={validateRequired}
          sx={{ width: 200 }} fullWidth={false}
        />
        <TextInput source="email" label="Email" validate={validateEmail} InputProps={{ autoComplete: 'off' }} sx={{ width: 320 }} fullWidth={false} />
        <TextInput source="contact" label="Contato" InputProps={{ autoComplete: 'off' }} sx={{ width: 250 }} fullWidth={false} />
        <TextInput source="emailNotice" label="Email para Avisos" validate={email()} InputProps={{ autoComplete: 'off' }} sx={{ width: 320 }} fullWidth={false} />
        <ImageInput
          source="logo"
          label="Logo"
          accept={{ 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'] }}
          validate={validateRequired}
        >
          <ImageField source="src" title="Logo" />
        </ImageInput>
      </FormTab>
      <FormTab label="Usuário">
        <TextInput source="username" label="Nome de Usuário" validate={[required()]} InputProps={{ autoComplete: 'off' }} sx={{ width: 250 }} fullWidth={false} />
        <TextInput source="login" label="Login" validate={[required()]} InputProps={{ autoComplete: 'off' }} sx={{ width: 200 }} fullWidth={false} />
        <TextInput source="password" label="Senha" validate={validatePassword} InputProps={{ autoComplete: 'new-password' }} sx={{ width: 200 }} fullWidth={false} />
      </FormTab>
      {
        Permission({
          permission: PERMISSIONS.REGISTER_CHAIN_MODULE,
          children: (
            <FormTab label='Módulos'>
              <Grid container spacing={2} sx={{ maxWidth: 800 }}>
                <Grid size={12}>
                  <Typography>
                    QrCode
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <PriceInput source="qrcodeValue" label="Valor" />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <BooleanInput source="useQrcode" label="Habilitar" />
                </Grid>
              </Grid>
              <Divider />
              <Grid container spacing={2} sx={{ maxWidth: 800 }}>
                <Grid size={12}>
                  <Typography>
                    Promoter
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <PriceInput source="promoterValue" label="Valor" />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <BooleanInput source="usePromoter" label="Habilitar" />
                </Grid>
              </Grid>
              <Divider />
              <Grid container spacing={2} sx={{ maxWidth: 800 }}>
                <Grid size={12}>
                  <Typography>
                    Anúncios
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <PriceInput source="advertisementValue" label="Valor" />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <BooleanInput source="useAdvertisement" label="Habilitar" />
                </Grid>
              </Grid>
              <Divider />
              <Grid container spacing={2} sx={{ maxWidth: 800 }}>
                <Grid size={12}>
                  <Typography>
                    Ads Pagos (AdMob) — Runtime
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Mudanças surtem efeito no próximo carregamento do app
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <BooleanInput source="hasPaidAds" label="Habilitar Ads Pagos" />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }} />
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextInput source="admobNativeUnitIdIos" label="AdMob Native Unit ID (iOS)" fullWidth />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextInput source="admobNativeUnitIdAndroid" label="AdMob Native Unit ID (Android)" fullWidth />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextInput source="admobMrecUnitIdIos" label="AdMob MREC Unit ID (iOS)" fullWidth />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextInput source="admobMrecUnitIdAndroid" label="AdMob MREC Unit ID (Android)" fullWidth />
                </Grid>
              </Grid>
              <Divider />
              <Grid container spacing={2} sx={{ maxWidth: 800 }}>
                <Grid size={12}>
                  <Typography>
                    Sorteios
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <PriceInput source="drawingValue" label="Valor" />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <BooleanInput source="useDrawing" label="Habilitar" />
                </Grid>
              </Grid>
              <Divider />
              <Grid container spacing={2} sx={{ maxWidth: 800 }} alignItems="center">
                <Grid size={12}>
                  <Typography>
                    Clube de Vantagens
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <PriceInput source="giftValue" label="Valor" />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <BooleanInput source="useGift" label="Habilitar" />
                </Grid>
              </Grid>
              <Divider />
              <Grid container spacing={2} sx={{ maxWidth: 800 }} alignItems="center">
                <Grid size={12}>
                  <Typography>
                    Bolão da Copa
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                  <BooleanInput source="useBolao" label="Habilitar bolão (mobile mostra cards e admin libera abas)" />
                </Grid>
              </Grid>
              <Divider />
              <Grid container spacing={2} sx={{ maxWidth: 800 }} alignItems="center">
                <Grid size={12}>
                  <Typography>
                    Roleta da Sorte
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                  <BooleanInput source="useRoleta" label="Habilitar Roleta da Sorte (app mostra a roleta e o admin libera o cadastro)" />
                </Grid>
              </Grid>
              <Divider />
              <Grid container spacing={2} sx={{ maxWidth: 800 }} alignItems="center">
                <Grid size={12}>
                  <Typography>
                    Dotz
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <PriceInput source="dotzValue" label="Valor" />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextInput
                    source="dotzFactor"
                    label="Fator Dotz"
                    defaultValue={0}
                    format={value => (!value ? '0,000' : `${Number(value).toFixed(3)}`)}
                    parse={value => (!value ? 0 : +value.replace(/[^\d]/g, '') / 1000)}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <BooleanInput source="useDotz" label="Habilitar" />
                </Grid>
              </Grid>
              <Divider />
              <Grid container spacing={2} sx={{ maxWidth: 800 }} alignItems="center">
                <Grid size={12}>
                  <Typography>
                    Meta de frentista
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <PriceInput source="attendantTargetValue" label="Valor" />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <BooleanInput source="useAttendantTarget" label="Habilitar" />
                </Grid>
              </Grid>
              <Divider />
              <Grid container spacing={2} sx={{ maxWidth: 800 }} alignItems="center">
                <Grid size={12}>
                  <Typography>
                    Limites de Pontuação
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <PriceInput source="limitsModuleValue" label="Valor" />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <BooleanInput label="Habilitar" source="useLimits" />
                </Grid>
              </Grid>
            </FormTab>
          )
        })
      }
      <FormTab label="Integrações">
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>SGA</Typography>
        <BooleanInput label="Habilitar SGA" source="useSgaIntegration" />
        <Divider />
        <WrapperDotz>
          <>
            <Grid container spacing={2} sx={{ maxWidth: 800 }} alignItems="flex-start">
              <Grid size={12}>
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                  Dotz
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextInput source="dotzUser" label="Usuário" fullWidth />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <PasswordInput source="dotzPassword" label="Senha" inputProps={{ autocomplete: 'current-password' }} fullWidth />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextInput
                  source="dotzFactor"
                  label="Fator de conversão"
                  defaultValue={0}
                  format={value => (!value ? '0,000' : `${Number(value).toFixed(3)}`)}
                  parse={value => (!value ? 0 : +value.replace(/[^\d]/g, '') / 1000)}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Divider />
          </>
        </WrapperDotz>
        <Grid container spacing={2} sx={{ maxWidth: 800 }} alignItems="flex-start">
          <Grid size={12}>
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
              Clube Certo
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <CNPJInput source="clubeCertoLogin" label="CNPJ" fullWidth />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <PasswordInput source="clubeCertoPassword" label="Senha" inputProps={{ autocomplete: 'new-password' }} fullWidth />
          </Grid>
        </Grid>
      </FormTab>
      <FormTab label='Configurações'>
        <TextInput source="firebaseProjectId" label="Id do Projeto no Firebase" InputProps={{ autoComplete: 'do-not-autofill' }} sx={{ width: 350 }} fullWidth={false} />
        <TextInput source="googlePackageName" label="Nome do Pacote no Google" InputProps={{ autoComplete: 'do-not-autofill' }} sx={{ width: 380 }} fullWidth={false} />
        <TextInput source="applePackageName" label="Nome do Pacote na Apple" InputProps={{ autoComplete: 'do-not-autofill' }} sx={{ width: 380 }} fullWidth={false} />
        <TextInput source="appleAppId" label="ID do Aplicativo na Apple" InputProps={{ id: 'field1', autoComplete: 'cc-exp' }} sx={{ width: 280 }} fullWidth={false} />
        <BooleanInput source="useCode2fa" label="Habilitar código temporário no app (Código 2FA)" />
        <BooleanInput label="Habilitar acréscimo" source="isPriceIncreaseEnabled" />
        <BooleanInput label="Habilitar descontos múltiplos" source="isMultipleDiscountsEnabled" />
        <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title="Quando marcado, o encerrante deverá ser informado obrigatoriamente em formulários como o de adição de pontos.">
          <BooleanInput label="Obrigar a informar encerrante" source="mustInformEncerrante" />
        </Tooltip>
        <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title="Quando marcado, o bico deverá ser informado obrigatoriamente em formulários como o de adição de pontos.">
          <BooleanInput label="Obrigar a informar bico" source="mustInformHoseNumber" />
        </Tooltip>
        <BooleanInput label="Usa Twilio" source="useTwilio" />
        <BooleanInput label="Usa Limites de Pontuação" source="useLimits" />
        <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title="Quando ativado, o aplicativo obrigará o envio de localização e a proximidade do cliente ao posto para fazer o abastecimento lendo o QrCode.">
          <RequireLocationInput />
        </Tooltip>
        <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title="Quando desativado, possibilitará que o cliente abasteça sem estar no posto por 15 minutos. Após esse prazo, essa opção ficará ativa novamente de forma automática.">
          <BooleanInput label="Conferir localização no abastecimento (desativação temporária de 15 minutos)" source="locationConfigEnabled" defaultValue={true} />
        </Tooltip>
        <BooleanInput label="Somente encher tanque" source="appOnlyCompletelyFillin" />
        <BooleanInput label="Usar QR Code na verificação de SignUp" source="appUseQrCodeForSignup" />
      </FormTab>
    </TabbedForm>
  </Create>
)
